import { escapeHtml } from './escapeHtml';

const ATTR_RE = /([^\s=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/gi;

function getAttr(attrs: string, name: string): string | undefined {
    ATTR_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = ATTR_RE.exec(attrs))) {
        if (match[1].toLowerCase() === name.toLowerCase()) {
            return match[2] ?? match[3] ?? match[4] ?? '';
        }
    }
    return undefined;
}

function stripAttr(attrs: string, name: string): string {
    return attrs
        .replace(new RegExp(`\\s*${name}\\s*=\\s*(?:"[^"]*"|'[^']*'|[^\\s>]+)`, 'i'), '')
        .replace(new RegExp(`\\s*${name}(?=[\\s>/]|$)`, 'i'), '');
}

function titleCaseWords(value: string): string {
    return value
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

/**
 * Turn a URL slug into readable copy, e.g. `b-c-collection` → `B C Collection`.
 */
export function titleFromSlug(slug: string): string {
    const cleaned = slug
        .replace(/\.[a-z0-9]+$/i, '')
        .replace(/[-_]?(\d+x)$/i, '')
        .replace(/^\d+[-_]?/, '')
        .replace(/[-_]+/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\d+$/g, '')
        .replace(/\s+/g, ' ')
        .trim();

    return titleCaseWords(cleaned);
}

/**
 * Accessible name for a promotional / brand image from its destination URL.
 * `/brands/anthem` → `Shop Anthem`, `/t-shirts` → `Shop T Shirts`.
 */
export function altFromUrl(url: string): string {
    if (!url) return '';

    try {
        const parsed = new URL(url, 'https://www.workwearexpress.com');
        const parts = parsed.pathname.split('/').filter(Boolean);
        const brandQuery = parsed.searchParams.get('brand');
        const last = parts[parts.length - 1] || '';
        const lastTitle = titleFromSlug(last.replace(/^all-/, ''));

        if (brandQuery) {
            return `Shop ${titleFromSlug(brandQuery)}${lastTitle ? ` ${lastTitle}` : ''}`.trim();
        }

        if (parts[0] === 'brands' && parts[1]) {
            const brand = titleFromSlug(parts[1]);
            if (parts.length > 2) {
                return `Shop ${titleFromSlug(last)}`;
            }
            return `Shop ${brand}`;
        }

        if (lastTitle) {
            return `Shop ${lastTitle}`;
        }
    } catch {
        // Fall through to filename-based copy below.
    }

    return '';
}

export function altFromSrc(src: string): string {
    if (!src) return '';
    const file = src.split('?')[0].split('/').pop() || '';
    return titleFromSlug(file);
}

function altForImage(attrs: string, fallback: string): string {
    const existing = getAttr(attrs, 'alt');
    if (existing && existing.trim()) {
        return existing.trim();
    }

    const src = getAttr(attrs, 'src') || '';
    return fallback || altFromSrc(src) || 'Image';
}

function rewriteImgs(html: string, fallback: string): string {
    return html.replace(/<img\b([^>]*?)(\s*\/?)>/gi, (_full, attrs: string, closing: string) => {
        const existing = getAttr(attrs, 'alt');
        if (existing && existing.trim()) {
            return `<img${attrs}${closing}>`;
        }
        const alt = altForImage(attrs, fallback);
        const cleaned = stripAttr(attrs, 'alt');
        const spacer = cleaned.length === 0 || /^\s/.test(cleaned) ? '' : ' ';
        return `<img${spacer}${cleaned} alt="${escapeHtml(alt)}"${closing}>`;
    });
}

/**
 * Ensure every `<img>` in Magento/CMS HTML has a non-empty alt.
 * Linked images use the destination (`Shop Anthem`); others fall back to the filename.
 * Existing non-empty alt attributes are left unchanged.
 */
export function ensureHtmlImageAlts(html: string): string {
    if (!html) return html;

    const withLinkedAlts = html.replace(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi, (_full, aAttrs: string, inner: string) => {
        const href = getAttr(aAttrs, 'href') || '';
        const fallback = altFromUrl(href);
        return `<a${aAttrs}>${rewriteImgs(inner, fallback)}</a>`;
    });

    return rewriteImgs(withLinkedAlts, '');
}
